
  import { defineComponent } from 'vue';
  import { mapActions, mapGetters } from 'vuex';

  import SectionTitle from '@/common/components/redesigned/SectionTitle/SectionTitle.vue';

  import EChartsHorizontalBar from '@/components/ChartComponents/apacheEcharts/EChartsHorizontalBar.vue';
  import Loader from '@/components/Loader.vue';

  import EconomicsReview from '@/modules/countryАnalytics/components/EconomicsReview/EconomicsReview.vue';
  import TecRussia from '@/modules/countryАnalytics/components/EconomicsReview/TecRussia.vue';

  import { COUNTRY_ANALYTICS_ACTIONS_NAME, COUNTRY_ANALYTICS_GETTERS_NAME } from '../../store/analytics/directoryNames';
  import TopCountryStatistic from '../countryStatistic/TopCountryStatistic.vue';
  import type { TCountrySeries, TGdpSeries, TGdpValueItem, TTopCountryItem } from './types';

  const ITEM_HEIGHT = 80;

  const OVERVIEW_CHART_COMMON_BIND = {
    valueFractionDigits: 1,
    xAxisMaxRatio: 1.2,
    categoryLabelWidth: 210,
    emptyMessage: 'Нет данных для показателя',
  } as const;

  export default defineComponent({
    name: 'EconomicOverview',
    components: {
      SectionTitle,
      EconomicsReview,
      EChartsHorizontalBar,
      TopCountryStatistic,
      TecRussia,
      Loader,
    },
    props: {
      countryId: {
        type: String,
        default: '',
      },
    },
    data(): {
      isLoading: boolean;
      gdpCategories: string[];
      importCountryData: TCountrySeries;
      exportCountryData: TCountrySeries;
      gdpSeries: TGdpSeries[];
    } {
      return {
        isLoading: false,
        gdpCategories: [],
        importCountryData: {
          labels: [],
          dataset: [],
        },
        exportCountryData: {
          labels: [],
          dataset: [],
        },
        gdpSeries: [
          {
            name: '',
            data: [],
          },
          {
            name: '',
            data: [],
          },
        ],
      };
    },
    computed: {
      ...mapGetters({
        getGdpValuesForeignTradeTurnoverWord: COUNTRY_ANALYTICS_GETTERS_NAME.getGdpValuesForeignTradeTurnoverWord,
        getIndustriesOfTopImport: COUNTRY_ANALYTICS_GETTERS_NAME.getIndustriesOfTopImport,
        getIndustriesOfTopExport: COUNTRY_ANALYTICS_GETTERS_NAME.getIndustriesOfTopExport,
        getCountriesOfTopImport: COUNTRY_ANALYTICS_GETTERS_NAME.getCountriesOfTopImport,
        getCountriesOfTopExport: COUNTRY_ANALYTICS_GETTERS_NAME.getCountriesOfTopExport,
        getInfoAboutUnfriendlyCountry: COUNTRY_ANALYTICS_GETTERS_NAME.getInfoAboutUnfriendlyCountry,
      }),
      gdpChartBind() {
        return {
          ...OVERVIEW_CHART_COMMON_BIND,
          showLegend: true,
          barMaxWidth: 28,
        };
      },
      topCountriesChartBind() {
        return {
          ...OVERVIEW_CHART_COMMON_BIND,
          showLegend: false,
          formatLabel: '%',
          barMaxWidth: 35,
          minWidth: 600,
        };
      },
      gdpDataset() {
        return [
          {
            data: this.gdpSeries[0].data,
            color: '#01A39D',
            label: this.gdpSeries[0].name,
          },
          {
            data: this.gdpSeries[1].data,
            color: '#D9D9D9',
            label: this.gdpSeries[1].name,
          },
        ];
      },
    },
    watch: {
      countryId() {
        this.loadData();
      },
    },
    created() {
      this.loadData();
    },
    methods: {
      ...mapActions({
        getForeignTradeTurnoverWordGdpValues: COUNTRY_ANALYTICS_ACTIONS_NAME.getForeignTradeTurnoverWordGdpValues,
        getTopImportIndustries: COUNTRY_ANALYTICS_ACTIONS_NAME.getTopImportIndustries,
        getTopExportIndustries: COUNTRY_ANALYTICS_ACTIONS_NAME.getTopExportIndustries,
        getTopImportCountries: COUNTRY_ANALYTICS_ACTIONS_NAME.getTopImportCountries,
        getTopExportCountries: COUNTRY_ANALYTICS_ACTIONS_NAME.getTopExportCountries,
      }),
      resolveChartHeight(labelsCount: number, withLegend = false): number {
        return Math.max(1, labelsCount) * ITEM_HEIGHT + (withLegend ? 56 : 24);
      },
      async loadData() {
        if (!(Number(this.countryId) > 0)) return;

        this.isLoading = true;

        await this.getForeignTradeTurnoverWordGdpValues({ countryId: this.countryId });
        this.buildGdpValuesForeignTradeTurnoverWord();

        await this.getTopImportIndustries({ countryId: this.countryId });
        await this.getTopExportIndustries({ countryId: this.countryId });
        await this.getTopImportCountries({ countryId: this.countryId });
        await this.getTopExportCountries({ countryId: this.countryId });

        this.importCountryData = this.buildCountrySeries(this.getCountriesOfTopImport);
        this.exportCountryData = this.buildCountrySeries(this.getCountriesOfTopExport);

        this.isLoading = false;
      },
      buildGdpValuesForeignTradeTurnoverWord() {
        this.gdpCategories = [];

        const currentYears: TGdpSeries['data'] = [];
        const previousYears: TGdpSeries['data'] = [];
        const currentDate = new Date();
        if (!this.getGdpValuesForeignTradeTurnoverWord || this.getGdpValuesForeignTradeTurnoverWord[0] == null) {
          this.gdpSeries[0].data = this.gdpSeries[1].data = [];
          return;
        }
        this.getGdpValuesForeignTradeTurnoverWord.forEach(
          ({ countryName, currentYearValue, previousYearValue }: TGdpValueItem) => {
            this.gdpCategories.push(countryName);

            currentYears.push(Number(currentYearValue) || 0);
            previousYears.push(Number(previousYearValue) || 0);
          },
        );

        this.gdpSeries = [
          { name: `${currentDate.getFullYear() - 1} г.`, data: previousYears },
          { name: `${currentDate.getFullYear()} г. (прогноз)`, data: currentYears },
        ];
      },
      buildCountrySeries(items: TTopCountryItem[]): TCountrySeries {
        const labels: string[] = [];
        const data: number[] = [];
        (items ?? []).forEach(({ key, value }) => {
          labels.push(key);
          data.push(Number(value) || 0);
        });
        return {
          labels,
          dataset: [
            {
              data,
              color: '#01A39D',
              label: '',
            },
          ],
        };
      },
    },
  });
